import type { CSSProperties } from 'react';
import '../components/atomic/layout/app_screen/app_screen.css';
import { containFitScale } from '../../../infrastructure/runtime';
import { useAppFit } from '../../hooks/useAppFit';
import { GoBackButton } from '../components/atomic/buttons/go_back_button/GoBackButton';
import { ScreenHeader } from '../components/atomic/layout/screen_header/ScreenHeader';
import { SkyAtmosphere } from '../components/composite/atmosphere/sky_atmosphere/SkyAtmosphere';
import type { TaskListScreenController } from './TaskListScreenController';
import type { TaskListScreenModel } from './TaskListScreenModelScreen';
import { HeaderText } from './texts/HeaderText';
import { TitleText } from './texts/TitleText';
import { TaskCard } from './task_card/TaskCard';
import './task_list_screen.css';
import type { ScreenViewProps } from '../shared/screenViewProps';
import { useSwipeNavigate } from '../shared/gestures/useSwipeNavigate';
import { InfoPopupButton } from '../components/atomic/buttons/info_popup_button/InfoPopupButton';
import { InfoPopupFrame2 } from '../components/atomic/dialog/info_popup_frame_2/InfoPopupFrame2';

import bodyTextSvg from './info_popup/body_text.svg';
import './info_popup/TicketsInfoPopup.css';
import titleTextSvg from './info_popup/title_text.svg';

export type TaskListScreenViewProps = ScreenViewProps<
  TaskListScreenModel,
  TaskListScreenController
>;

/** Task list screen (Figma tasks 160:16785). */
export function TaskListScreenView({ model, controller }: TaskListScreenViewProps) {
  const swipe = useSwipeNavigate({
    axis: 'vertical',
    onSwipe: controller.handleNavigate,
    enabled: !model.infoOpen,
  });
  const fit = useAppFit();
  const contain = containFitScale(fit.scale);

  return (
    <div
      className="app-screen task-list-screen"
      style={swipe.style}
      onPointerDown={swipe.onPointerDown}
      onPointerUp={swipe.onPointerUp}
      onPointerCancel={swipe.onPointerCancel}
      onClickCapture={swipe.onClickCapture}
    >
      <SkyAtmosphere variant="delivery" />
      <div
        className="task-list-plaques"
        style={
          {
            '--fit-contain-inv-x': String(contain.invX),
            '--fit-contain-inv-y': String(contain.invY),
            '--fit-contain-uniform': String(contain.uniform),
          } as CSSProperties
        }
      >
        <ScreenHeader showSubHeader={false} />
        <HeaderText />
        <TitleText />
        <InfoPopupButton
          className={model.infoOpen ? 'info-popup-button--above-popup' : undefined}
          left={919}
          top={162}
          scale={1.503672}
          onClick={controller.handleInfoButtonClick}
        />

        {model.tasks.map((task, index) => (
          <TaskCard
            key={task.top}
            top={task.top}
            taskNameSrc={task.taskNameSrc}
            taskNameAlt={task.taskNameAlt}
            taskNameLayout={task.taskNameLayout}
            rewardValueSrc={task.rewardValueSrc}
            rewardValueAlt={task.rewardValueAlt}
            rewardValueLayout={task.rewardValueLayout}
            completed={task.completed}
            onClick={() => controller.handleTaskClick(index)}
          />
        ))}
        {model.infoOpen ? (
          <InfoPopupFrame2
            titleSrc={titleTextSvg}
            bodySrc={bodyTextSvg}
            titleClassName="tickets-info-popup__title"
            bodyClassName="tickets-info-popup__body"
            titleAlt="A flight ticket is your second chance!"
            bodyAlt="Complete urgent orders and tasks to stock retries. Use them to replay a delivery and improve the result."
            onClose={controller.handleInfoClose}
          />
        ) : null}
        <GoBackButton onClick={controller.handleGoBack} />
      </div>
    </div>
  );
}
